/**
 * PatternBadge (DC-1492, reworked v2.5 SS-03 / PR C1 round 1 — G0 §6).
 *
 * Named-pattern headline with the confidence rendered VISIBLY from the two
 * independent dimensions (evidence basis + coverage confidence), plus a
 * keyboard-accessible disclosure with the full explanation. G0 prohibits
 * tooltip-only information: nothing here relies on `title`.
 *
 * Composition (G0 §6.1, basis first, middle-dot joined):
 *   - full + sufficient            → "Confirmed"
 *   - full + preliminary           → "Provisional · Technical coverage preliminary (26%)"
 *   - documented_missing           → "Suspected · Perceived coverage preliminary (27%)"
 *   - documented_declared_absent + sufficient → "Confirmed by the two
 *     available legs", with the basis sentence (who declared, when) beneath
 *   - documented_declared_absent + preliminary → "Provisional · …" (coverage
 *     outranks the basis — mirrors deriveQualifier); attributed absence
 *     sentence still beneath
 *   - dimensions not recorded      → "Legacy interpretation" (pre-v2.5
 *     snapshot; both dimensions read "Not recorded for this reading")
 *
 * The badge also renders the two "no pattern emitted" states (unchanged):
 *   - hardFloorBreached → "Insufficient data"
 *   - otherwise         → "Mixed / under review"
 */

import React from 'react';
import type {
  CoverageConfidence,
  EvidenceBasis,
  PatternName,
  PatternQualifier,
} from '../../types/triangulation';
import { declaredAbsentSentence, type PreliminaryLegDetail } from './legUtils';

interface PatternBadgeProps {
  pattern: PatternName | null;
  qualifier: PatternQualifier;
  /** v2.5 SS-03: the two independent confidence dimensions. */
  evidenceBasis?: EvidenceBasis | null;
  coverageConfidence?: CoverageConfidence | null;
  /** Contributing legs below their band, with G0 coverage phrases. */
  preliminaryLegDetails?: PreliminaryLegDetail[];
  /** Methodology that produced the dimensions (metadata.thresholds). */
  methodologyVersion?: string | null;
  /** Declared-absence attribution (domain.documented_state_set_by_*). */
  declaredByName?: string | null;
  declaredAt?: string | null;
  /**
   * Whether the cell's hard-floor was breached (Tech or Perceived
   * not_yet_gathered or below coverage floor). Drives which "no pattern"
   * label renders when pattern === null.
   */
  hardFloorBreached?: boolean;
}

/** Pattern → visual family (matches the design doc's intent). */
type Family = 'good' | 'soft' | 'hard' | 'neutral';

const PATTERN_FAMILY: Record<PatternName, Family> = {
  'Confirmed Maturity': 'good',
  'Aligned Maturity': 'good',
  Greenfield: 'neutral',
  'Unformalized Practice': 'soft',
  'Tribal Knowledge': 'soft',
  'Usability Gap': 'hard',
  'Abandoned Capability': 'hard',
  'Policy Theater': 'hard',
};

const FAMILY_VAR: Record<Family, string> = {
  good: 'var(--tri-good)',
  soft: 'var(--tri-soft)',
  hard: 'var(--tri-hard)',
  neutral: 'var(--tri-neutral)',
};

export const PatternBadge: React.FC<PatternBadgeProps> = ({
  pattern,
  qualifier,
  evidenceBasis = null,
  coverageConfidence = null,
  preliminaryLegDetails = [],
  methodologyVersion = null,
  declaredByName = null,
  declaredAt = null,
  hardFloorBreached = false,
}) => {
  // pattern === null → "Insufficient data" or "Mixed / under review"
  if (pattern === null) {
    const label = hardFloorBreached ? 'Insufficient data' : 'Mixed / under review';
    return (
      <div
        data-testid="pattern-badge"
        data-pattern="none"
        data-empty-reason={hardFloorBreached ? 'insufficient' : 'mixed'}
        className="tri-pattern fam-neutral"
      >
        <span className="tri-qualifier confirmed fam-neutral" aria-hidden="true" />
        <span className="tri-pattern-name">{label}</span>
        <span className="tri-basis-line">
          {hardFloorBreached
            ? 'Tech or Perceived leg is missing or below its coverage hard floor — collect more signal to surface a pattern.'
            : 'At least one leg lands in the mid-bucket (2.5–3.5) — waiting on stronger signal.'}
        </span>
      </div>
    );
  }

  const family = PATTERN_FAMILY[pattern];
  const famVar = FAMILY_VAR[family];
  const isLegacy = evidenceBasis === null || coverageConfidence === null;
  const solidDot = qualifier === 'confirmed' && !isLegacy;
  const dotStyle: React.CSSProperties = solidDot
    ? { background: famVar }
    : { borderColor: famVar, borderStyle: 'solid', borderWidth: 2, background: 'transparent' };

  // G0 §6.1 composed confidence line — ALWAYS VISIBLE, never hover-only.
  // Round 2: PRELIMINARY COVERAGE OUTRANKS THE DECLARED-ABSENT BASIS —
  // mirrors the server's deriveQualifier (suspected > provisional >
  // confirmed). "Confirmed by the two available legs" renders only at
  // sufficient coverage; the attributed declared-absence sentence still
  // renders beneath regardless of coverage.
  const coveragePhrases = preliminaryLegDetails.map((d) => d.phrase);
  const baseWord = isLegacy
    ? 'Legacy interpretation'
    : evidenceBasis === 'documented_missing'
      ? 'Suspected'
      : coverageConfidence === 'preliminary'
        ? 'Provisional'
        : evidenceBasis === 'documented_declared_absent'
          ? 'Confirmed by the two available legs'
          : 'Confirmed';
  const confidenceLine =
    coverageConfidence === 'preliminary' && coveragePhrases.length > 0
      ? [baseWord, ...coveragePhrases].join(' · ')
      : baseWord;

  // The basis sentence beneath (G0: declared-absent names who and when).
  const basisSentence = isLegacy
    ? 'Recorded before the v2.5 confidence methodology — evidence basis and coverage confidence were not recorded for this reading.'
    : evidenceBasis === 'documented_declared_absent'
      ? declaredAbsentSentence(declaredByName, declaredAt)
      : evidenceBasis === 'documented_missing'
        ? 'Documented signal has not been gathered yet — this is a bilateral read from Technical and Perceived; it may resolve to a different pattern once documents are reviewed.'
        : coverageConfidence === 'preliminary'
          ? 'All contributing legs scored, but coverage has not reached its sufficiency band.'
          : 'All three legs contributed scored signal at sufficient coverage.';

  return (
    <div
      data-testid="pattern-badge"
      data-pattern={pattern}
      data-qualifier={qualifier ?? 'none'}
      data-evidence-basis={evidenceBasis ?? 'none'}
      data-coverage-confidence={coverageConfidence ?? 'none'}
      className={`tri-pattern fam-${family}`}
    >
      <span
        className={`tri-qualifier ${solidDot ? 'confirmed' : 'suspected'} fam-${family}`}
        style={dotStyle}
        aria-hidden="true"
      />
      <span className="tri-pattern-name">{pattern}</span>
      {/* VISIBLE confidence — both dimensions, never one word hiding the
          other, never tooltip-only (G0 §6). */}
      <span className="tri-basis-line" data-testid="pattern-confidence">
        {confidenceLine}
      </span>
      {/* Keyboard-accessible disclosure: full explanation + methodology. */}
      <details className="tri-confidence-details">
        <summary>Why this confidence?</summary>
        <p>{basisSentence}</p>
        {coveragePhrases.length > 0 && (
          <ul>
            {coveragePhrases.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}
        <p className="tri-methodology">
          {isLegacy
            ? 'Methodology: recorded before pattern-qualifier/2.0.'
            : `Methodology: ${methodologyVersion ?? 'pattern-qualifier/2.0'}.`}
        </p>
      </details>
    </div>
  );
};
